import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PreflightKey } from '@hcc/types';
import { api } from '../lib/api';

export interface OnboardingDocVersion {
  id: string;
  version: number;
  title: string;
  requires_option: boolean;
  requires_resign: boolean;
  options: DocOption[];
  published_at: string | null;
  created_at: string;
}

export interface OnboardingDoc {
  id: string;
  key: string;
  title: string;
  requires_approval: boolean;
  preflight_key: PreflightKey | null;
  /** Document-level signing config (persists across version uploads). */
  requires_option: boolean;
  options: DocOption[];
  require_read: boolean;
  /** Custom acknowledgement wording; null = the default confirmation text. */
  ack_text: string | null;
  archived_at: string | null;
  versions: OnboardingDocVersion[];
}

const KEY = ['onboarding-documents'] as const;

export function useOnboardingDocuments() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api<{ documents: OnboardingDoc[] }>('/api/admin/onboarding-documents'))
        .documents,
  });
}

export function useCreateOnboardingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; title: string }) =>
      api<{ document: OnboardingDoc }>('/api/admin/onboarding-documents', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOnboardingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: {
      id: string;
      title?: string;
      requires_approval?: boolean;
      preflight_key?: PreflightKey | null;
      requires_option?: boolean;
      options?: DocOption[];
      require_read?: boolean;
      ack_text?: string | null;
    }) =>
      api<{ document: OnboardingDoc }>(`/api/admin/onboarding-documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface DocOption {
  value: string;
  label: string;
}

export function useUploadOnboardingVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      title: string;
      pdf_base64: string;
      filename?: string;
      requires_resign?: boolean;
      requires_option?: boolean;
      options?: DocOption[];
    }) =>
      api(`/api/admin/onboarding-documents/${input.id}/versions`, {
        method: 'POST',
        body: JSON.stringify({
          title: input.title,
          pdf_base64: input.pdf_base64,
          filename: input.filename,
          requires_resign: input.requires_resign ?? false,
          requires_option: input.requires_option ?? false,
          options: input.options ?? [],
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useArchiveOnboardingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/onboarding-documents/${id}/archive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
